/**
 * Timer Modal Component - Generic Library Component
 *
 * A reusable countdown timer modal component that displays a timer interface
 * with increment/decrement controls and start/stop buttons.
 *
 * **Design Philosophy:**
 * - Zero business logic - all validation and completion handling via callbacks
 * - Zero SimManager dependencies - app handles all state management
 * - Generic timer UI that can be used for any simulation
 *
 * **Usage:**
 * The consuming app component should:
 * 1. Provide validation logic in onStartTimer callback
 * 2. Handle timer completion in onTimerComplete callback
 * 3. Optionally customize time increments
 *
 * @example
 * ```tsx
 * <TimerModal
 *   show={true}
 *   close={handleClose}
 *   timerImage={TimerImage}
 *   onStartTimer={validateAndStart}
 *   onTimerComplete={handleComplete}
 *   hourIncrement={3600}
 *   minuteIncrement={300}
 * />
 * ```
 */
import React from "react";
import { FullscreenModal } from "@learningblocks/storybook-components";
import { isActivationKey } from "@learningblocks/shared-components";

import "./Timer.scss";

/**
 * Props for the TimerModal component
 */
export interface TimerModalProps {
  // Modal control
  show: boolean;
  close: () => void;

  // Timer image
  timerImage: string; // App provides timer image

  // Callbacks for app-specific logic
  onStartTimer: (currentTime: number) => boolean; // Returns true if validation passes
  onTimerComplete: (totalTime: number) => void; // Called when timer reaches zero
  onTimerTick?: (currentTime: number) => void; // Optional callback on each tick

  // Configuration
  hourIncrement?: number; // Seconds to add/remove per hour button (default: 3600)
  minuteIncrement?: number; // Seconds to add/remove per minute button (default: 300)
  tickInterval?: number; // Milliseconds between ticks (default: 1000)

  // Display configuration
  title?: string;
  ariaLabel?: string;
}

/**
 * State for the TimerModal component
 */
interface TimerModalState {
  setTime: number; // Current time in seconds
  totalTime: number; // Total time that was set when timer started
  isRunning: boolean; // Whether timer is currently running
}

/**
 * TimerModal Component
 *
 * Renders a generic timer UI with:
 * - Hour increment/decrement buttons
 * - Minute increment/decrement buttons
 * - Start/Stop buttons
 * - Digital time display (HH:MM:SS)
 * - Timer image overlay
 *
 * All business logic (validation, completion handling) is provided by the app via callbacks.
 */
export class TimerModal extends React.Component<
  TimerModalProps,
  TimerModalState
> {
  private interval: any;

  static defaultProps = {
    hourIncrement: 3600,
    minuteIncrement: 300,
    tickInterval: 1000,
    title: "Timer",
    ariaLabel: "A timer, which has a screen with different buttons on it.",
  };

  constructor(props: TimerModalProps) {
    super(props);

    this.state = {
      setTime: 0,
      totalTime: 0,
      isRunning: false,
    };

    // Initialize empty interval
    this.interval = setInterval(() => {
      clearInterval(this.interval);
    }, 0);
  }

  componentWillUnmount() {
    // Clean up interval on unmount
    this.stopTimer();
  }

  /**
   * Start the countdown timer
   * Calls onStartTimer callback for validation before starting
   */
  startTimer = () => {
    // Don't start if already running or time is 0
    if (this.state.isRunning || this.state.setTime === 0) {
      return;
    }

    // Call app's validation callback
    const validationPassed = this.props.onStartTimer(this.state.setTime);
    if (!validationPassed) {
      return; // Validation failed, don't start timer
    }

    // Validation passed, start the timer
    let totalSeconds = this.state.setTime;
    this.setState({
      totalTime: this.state.setTime,
      isRunning: true,
    });

    this.interval = setInterval(() => {
      if (totalSeconds <= 0) {
        this.stopTimer();
        // Call completion callback
        this.props.onTimerComplete(this.state.totalTime);
        return;
      }

      totalSeconds -= 1;
      this.setState({ setTime: totalSeconds });

      // Optional tick callback
      if (this.props.onTimerTick) {
        this.props.onTimerTick(totalSeconds);
      }
    }, this.props.tickInterval);
  };

  /**
   * Stop the countdown timer
   */
  stopTimer = () => {
    clearInterval(this.interval);
    this.setState({ isRunning: false });
  };

  /**
   * Increment time by configured hour amount
   */
  incrementHours = () => {
    if (!this.state.isRunning) {
      this.setState({
        setTime: this.state.setTime + this.props.hourIncrement!,
      });
    }
  };

  /**
   * Decrement time by configured hour amount
   */
  decrementHours = () => {
    if (
      !this.state.isRunning &&
      this.state.setTime >= this.props.hourIncrement!
    ) {
      this.setState({
        setTime: this.state.setTime - this.props.hourIncrement!,
      });
    }
  };

  /**
   * Increment time by configured minute amount
   */
  incrementMinutes = () => {
    if (!this.state.isRunning) {
      this.setState({
        setTime: this.state.setTime + this.props.minuteIncrement!,
      });
    }
  };

  /**
   * Decrement time by configured minute amount
   */
  decrementMinutes = () => {
    if (!this.state.isRunning) {
      if (this.state.setTime > this.props.minuteIncrement!) {
        this.setState({
          setTime: this.state.setTime - this.props.minuteIncrement!,
        });
      } else {
        this.setState({ setTime: 0 });
      }
    }
  };

  /**
   * Format number to have leading zero if less than 10
   */
  formatNumber(number: number): string {
    return number < 10 ? `0${number}` : `${number}`;
  }

  render() {
    const { show, close, timerImage, ariaLabel } = this.props;

    // Calculate hours, minutes, seconds from setTime
    const hours = this.formatNumber(Math.floor(this.state.setTime / 3600));
    const minutes = this.formatNumber(
      Math.floor((this.state.setTime % 3600) / 60)
    );
    const seconds = this.formatNumber(this.state.setTime % 60);

    return (
      <FullscreenModal
        className="modalContainer"
        isOpen={show}
        closeButton={true}
        notebookExpanded={false}
        close={close}
        backLabel="Lab"
      >
        <div className="Timer">
          <div aria-label={ariaLabel} className="TimerImage">
            <img src={timerImage} alt="Timer" />

            {/* Hour increment button */}
            <div
              aria-label="Increase by one hour"
              className="HoursPlus"
              role="button"
              tabIndex={0}
              onClick={this.incrementHours}
              onKeyDown={(e) => {
                isActivationKey(e) && this.incrementHours();
              }}
            />

            {/* Hour decrement button */}
            <div
              aria-label="Decrease by one hour"
              className="HoursMinus"
              role="button"
              tabIndex={0}
              onClick={this.decrementHours}
              onKeyDown={(e) => {
                isActivationKey(e) && this.decrementHours();
              }}
            />

            {/* Minute increment button */}
            <div
              aria-label="Increase by five minutes"
              className="MinutesPlus"
              role="button"
              tabIndex={0}
              onClick={this.incrementMinutes}
              onKeyDown={(e) => {
                isActivationKey(e) && this.incrementMinutes();
              }}
            />

            {/* Minute decrement button */}
            <div
              aria-label="Decrease by five minutes"
              className="MinutesMinus"
              role="button"
              tabIndex={0}
              onClick={this.decrementMinutes}
              onKeyDown={(e) => {
                isActivationKey(e) && this.decrementMinutes();
              }}
            />

            {/* Start button */}
            <div
              aria-label="Start"
              className="Start"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                isActivationKey(e) && this.startTimer();
              }}
              onClick={this.startTimer}
            />

            {/* Stop button */}
            <div
              aria-label="Stop"
              className="Stop"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                isActivationKey(e) && this.stopTimer();
              }}
              onClick={this.stopTimer}
            />

            {/* Time display */}
            <div
              aria-label={`Screen reading ${hours}:${minutes}:${seconds}`}
              className="text"
            >
              {hours} : {minutes} : {seconds}
            </div>
          </div>
        </div>
      </FullscreenModal>
    );
  }
}
